import type { LiteUserOut } from '@repo/api-codegen';
import type { SelectablePropertyItem } from '@repo/common/components/SingleSelector/types';
import { pickColorType } from '@repo/common/utils';
import { IconUsers } from '@tabler/icons-react';
import { uniqBy } from 'lodash-es';
import type { IUserGroup } from '../../api';
import type { DjangoValueType } from '../../pages/TemplatePage/types';
import type { OverviewPageTab } from '../Overview/OverviewPageTabs';
import type { SidebarEntityKeys } from './types';

export const getOwnerOptions = (
	users: LiteUserOut[],
	onOwnerClick: (value: any) => void,
	hasGroup = true
) =>
	uniqBy(users, (user) => user.id).map((owner) => {
		const colorType = pickColorType(owner.display_name, owner.disabled);
		const item = {
			label: owner.display_name,
			value: owner.id,
			icon: owner.profile_picture,
			navigateTo: `/user/${owner.id}`,
			onClick: onOwnerClick,
			strikeThrough: owner.disabled,
			hidden: false,
			color: colorType,
		} as SelectablePropertyItem;

		if (hasGroup) {
			item.group = 'users';
		}

		return item;
	});

export const getGroupOptions = (
	groups: IUserGroup[],
	onGroupClick: (value: any) => void,
	hasGroup = true
) =>
	uniqBy(groups, (group) => group.id).map((group) => {
		const item = {
			label: group.name,
			value: group.id,
			defaultIcon: IconUsers,
			navigateTo: `/group/${group.id}`,
			onClick: onGroupClick,
		} as SelectablePropertyItem;

		if (hasGroup) {
			item.group = 'groups';
		}

		return item;
	});

export const getOwnerAndGroupSelectorOptions = (
	users: LiteUserOut[],
	groups: IUserGroup[],
	onOwnerClick: (value: any) => void,
	onGroupClick: (value: any) => void
) => {
	const ownerOptions = getOwnerOptions(users, onOwnerClick);
	const groupOptions = getGroupOptions(groups, onGroupClick);

	return [...ownerOptions, ...groupOptions];
};

export const getAssigneeOptions = (
	users: LiteUserOut[],
	onClick: (value: any) => void
) =>
	uniqBy(users, (user) => user.id).map((owner) => {
		const item = {
			label: owner.display_name,
			value: owner.id,
			icon: owner.profile_picture,
			navigateTo: `/user/${owner.id}`,
			onClick: onClick,
			strikeThrough: owner.disabled,
			hidden: owner.disabled,
			group: 'users',
		} as SelectablePropertyItem;
		return item;
	});
export const getAssigneeGroupOptions = (
	groups: IUserGroup[],
	onClick: (value: any) => void
) =>
	uniqBy(groups, (group) => group.id).map((group) => {
		const item = {
			label: group.name,
			value: group.id,
			defaultIcon: IconUsers,
			navigateTo: `/group/${group.id}`,
			onClick: onClick,
			group: 'groups',
		} as SelectablePropertyItem;
		return item;
	});
export const getAssigneeAndGroupSelectorOptions = (
	users: LiteUserOut[],
	groups: IUserGroup[],
	onClick: (
		key: SidebarEntityKeys
	) => (value: DjangoValueType, saveRemotely?: boolean) => void
) => {
	const ownerOptions = getAssigneeOptions(users, (value: DjangoValueType) => {
		onClick('assigned_to_group')(null, false);
		onClick('assigned_to')(value, true);
	});
	const groupOptions = getAssigneeGroupOptions(
		groups,
		(value: DjangoValueType) => {
			onClick('assigned_to_group')(value, true);
			onClick('assigned_to')(null, false);
		}
	);
	return [...ownerOptions, ...groupOptions];
};

export const getDefaultTab = (tabs: OverviewPageTab[]) => {
	const visibleTabs = (tabs || []).filter((t) => t.condition !== false);
	return visibleTabs[0]?.value ?? 'documentation';
};

export const getOrderedTabs = (
	tabs: OverviewPageTab[],
	documentationFirst: boolean
): OverviewPageTab[] => {
	if (!documentationFirst) return tabs;
	const index = tabs.findIndex((t) => t.value === 'documentation');
	if (index === -1) return tabs;
	const copy = [...tabs];
	const [docTab] = copy.splice(index, 1);
	copy.unshift(docTab);
	return copy;
};
